import { useState, useRef, useCallback, useEffect } from 'react';
import toast from 'react-hot-toast';

import api from '../../utils/api';
import { mergeMensagem } from '../../utils/chatUtils';
import { uploadFileChunked } from '../../utils/chunkedUpload';

/**
 * Upload de anexos + gravação de áudio do widget de chat.
 * Encapsula estado/refs/efeitos da feature; recebe o contexto do canal por parâmetro e
 * devolve os handlers que a UI consome. Movimento puro de ChatWidget — sem mudança de lógica.
 */
export function useChatAnexos({ aberto, naRotaChat, canalAtivo, canalAtivoRef, meuId, setMensagens }) {
  // ---------- upload ----------
  const [gravando, setGravando] = useState(false);
  const [arrastando, setArrastando] = useState(false);
  const fileInputRef = useRef(null);
  const mediaRecRef = useRef(null);
  const audioChunksRef = useRef([]);
  const cancelandoRef = useRef(false);      // lido DENTRO do onstop (ver iniciarGravacao)
  const canalGravacaoRef = useRef(null);    // canal onde a gravação começou
  const iniciandoRef = useRef(false);       // guard SÍNCRONO: cobre a janela do getUserMedia
  const desmontadoRef = useRef(false);      // desmontou com o prompt de permissão aberto

  // Painel fechado (ou rota /chat, onde o widget some) = não grava. Lido DEPOIS do await do
  // getUserMedia, quando o valor capturado na closure já pode estar velho.
  const podeGravarRef = useRef(false);
  podeGravarRef.current = aberto && !naRotaChat && !!canalAtivo;

  const enviarArquivo = async (file, canalIdAlvo) => {
    // canalIdAlvo: usado pela gravação de áudio (o canal pode ter mudado durante a gravação).
    const alvo = canalIdAlvo || canalAtivoRef.current?.id;
    if (!file || !alvo) return;
    // Espelho leve do servidor (utils/upload_validation.EXT_PERIGOSA): o chat aceita
    // QUALQUER formato, menos executável/script. Só para dar erro na hora — quem manda é
    // o servidor, que ainda confere o conteúdo real (magic bytes).
    const extBloqueada = /\.(exe|dll|msi|com|scr|pif|cpl|jar|msc|bat|cmd|ps1|psm1|vbs|vbe|js|jse|wsf|wsh|hta|sh|bash|zsh|py|pl|rb|php|jsp|asp|aspx|apk|app|deb|rpm|dmg|iso|img|lnk|reg|scf|inf|gadget|url|chm|jnlp|docm|xlsm|xlsb|pptm|dotm|xltm|potm)$/i.test(file.name || '');
    if (extBloqueada) { toast.error('Arquivo executável ou script não é permitido'); return; }
    const tid = toast.loading('Enviando arquivo...');
    try {
      // Upload em chunks (256KB) → contorna o limite de corpo do proxy/ingress. validate="anexo":
      // o backend confere magic bytes no /complete.
      const up = await uploadFileChunked(file, (pct) => toast.loading(`Enviando... ${pct}%`, { id: tid }), 'anexo');
      const name = (file.name || '').toLowerCase();
      const mt = (file.type || '').toLowerCase();
      const tipo = (mt.startsWith('image/') || /\.(jpe?g|png|gif|webp|bmp)$/.test(name)) ? 'imagem'
        : (mt.startsWith('audio/') || /\.(mp3|m4a|aac|ogg|oga|opus|wav|weba|flac|amr)$/.test(name)) ? 'audio'
        : (mt.startsWith('video/') || /\.(mp4|m4v|mov|webm|avi|mkv|3gpp?)$/.test(name)) ? 'video'
        : 'arquivo';
      const anexo = {
        url: up.url,
        nome: file.name,
        tamanho: up.size ?? file.size,
        mime_type: file.type || up.content_type || '',
        storage_path: up.storage_path,
      };
      const clientMsgId = (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : `${meuId || 'u'}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      const res = await api.post(`/chat/canais/${alvo}/mensagens`, { tipo, anexo, client_msg_id: clientMsgId });
      // Só injeta na lista se a conversa EXIBIDA AGORA é a de destino (ref, não closure).
      if (canalAtivoRef.current?.id === alvo) setMensagens(prev => mergeMensagem(prev, res.data));
      toast.success('Arquivo enviado', { id: tid });
    } catch (e) {
      toast.error(e?.response?.status === 415 ? 'Tipo de arquivo não permitido' : 'Falha no upload', { id: tid });
    }
  };

  // ---------- gravação de áudio ----------
  const iniciarGravacao = async () => {
    // O guard precisa ser SÍNCRONO: `gravando`/`mediaRecRef` só ficam preenchidos DEPOIS do
    // await abaixo, então sem `iniciandoRef` dois cliques durante o prompt de permissão
    // criariam 2 streams — e o primeiro ficaria órfão com o microfone ligado para sempre.
    if (gravando || mediaRecRef.current || iniciandoRef.current) return;
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      toast.error('Gravação de áudio não é suportada neste navegador'); return;
    }
    iniciandoRef.current = true;
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      iniciandoRef.current = false;
      toast.error('Não foi possível acessar o microfone'); return;
    }
    if (desmontadoRef.current || !podeGravarRef.current) {
      // Fechou o painel (ou desmontou) com o prompt aberto: desliga o microfone e desiste.
      stream.getTracks().forEach(t => t.stop());
      iniciandoRef.current = false; return;
    }
    try {
      const rec = new MediaRecorder(stream);
      audioChunksRef.current = [];
      cancelandoRef.current = false;
      canalGravacaoRef.current = canalAtivoRef.current?.id || null;
      rec.ondataavailable = (e) => { if (e.data && e.data.size) audioChunksRef.current.push(e.data); };
      rec.onstop = () => {
        stream.getTracks().forEach(t => t.stop());  // desliga o microfone
        const chunks = audioChunksRef.current;
        audioChunksRef.current = [];
        // CANCELAR precisa de flag: stop() dispara 'dataavailable' com TODO o áudio ANTES
        // do 'stop', então limpar o buffer antes não cancela nada (ele é reenchido aqui).
        if (cancelandoRef.current) { cancelandoRef.current = false; return; }
        const blob = new Blob(chunks, { type: rec.mimeType || 'audio/webm' });
        if (!blob.size) return;
        const mt = (rec.mimeType || '').toLowerCase();
        const ext = mt.includes('ogg') ? 'ogg' : (mt.includes('mp4') || mt.includes('m4a')) ? 'm4a' : 'webm';
        // envia para o canal onde a gravação começou (o usuário pode ter trocado de conversa)
        enviarArquivo(new File([blob], `audio-${Date.now()}.${ext}`, { type: blob.type }), canalGravacaoRef.current);
      };
      mediaRecRef.current = rec;
      rec.start();
      setGravando(true);
    } catch {
      stream.getTracks().forEach(t => t.stop());  // não deixa o microfone ligado
      mediaRecRef.current = null;                 // não trava gravações futuras
      toast.error('Não foi possível iniciar a gravação');
    } finally {
      iniciandoRef.current = false;
    }
  };

  const pararGravacao = useCallback((cancelar) => {
    const rec = mediaRecRef.current;
    cancelandoRef.current = !!cancelar;
    mediaRecRef.current = null;
    setGravando(false);
    if (rec) { try { rec.stop(); } catch {} }
  }, []);

  // Sair da conversa (voltar para a lista), fechar o painel ou entrar em /chat durante a
  // gravação: descarta e desliga o microfone. Os botões de parar/cancelar vivem no ramo
  // CONVERSA — sem canalAtivo eles somem da tela e o microfone ficaria gravando sem que o
  // usuário tenha como pará-lo. A condição espelha `podeGravarRef` de propósito.
  useEffect(() => {
    if (aberto && !naRotaChat && canalAtivo) return;
    if (mediaRecRef.current) pararGravacao(true);
  }, [aberto, naRotaChat, canalAtivo, pararGravacao]);

  // Ao desmontar durante a gravação: descarta e desliga o microfone. desmontadoRef também
  // cobre desmontar com o getUserMedia ainda pendente — aí o próprio iniciarGravacao para as
  // tracks ao resolver (o rec ainda nem existe aqui).
  useEffect(() => () => {
    desmontadoRef.current = true;
    const rec = mediaRecRef.current;
    if (rec) { cancelandoRef.current = true; try { rec.stop(); } catch {} mediaRecRef.current = null; }
  }, []);

  // ---------- arrastar-para-enviar / colar ----------
  const onDropArquivo = async (e) => {
    const files = Array.from(e.dataTransfer?.files || []);
    setArrastando(false);                 // sempre some com o overlay
    if (!files.length) return;            // drag de texto/link: deixa o comportamento padrão
    e.preventDefault();
    if (files.length > 1) toast(`Enviando ${files.length} arquivos...`);
    // Sequencial: N uploads em paralelo estouram o rate-limit de envio.
    for (const f of files) await enviarArquivo(f);
  };

  const onPasteArquivo = (e) => {
    const files = Array.from(e.clipboardData?.files || []);
    if (!files.length) return;   // colando texto: deixa o comportamento padrão do textarea
    e.preventDefault();
    (async () => { for (const f of files) await enviarArquivo(f); })();
  };

  return {
    gravando, arrastando, setArrastando, fileInputRef,
    enviarArquivo, iniciarGravacao, pararGravacao, onDropArquivo, onPasteArquivo,
  };
}

export default useChatAnexos;
